var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * [CLAIMS_CONTEXT]
 * purpose: Allow the user to select the type of damages caused by a glass breakage.
 * scope: Glass breakage qualification step where the user specifies whether the damage affects the room, personal properties, or both.
 * invariants: Selected glass breakage damage types remain synchronized with the current glass breakage qualification.
 * inputs: Available glass breakage damage types with illustrations, insurer settings, claim type information, and user selection.
 * outputs: Updates glass breakage qualification fields (hasRoomDamages and hasPersonalPropertyDamages) through updateQualification and saves the qualification through updateAndSaveQualification.
 * reference: frontend/src/declaration/components/glassBreakageDamage
 */
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { UiButton, UiCheckbox } from '@claims-ia/design-system';
import { QuestionComponent, QuestionProps } from '@declaration/question';
import QuestionPanel from '@declaration/components/UI/QuestionPanel.vue';
import { GLASS_BREAKAGE_DAMAGE_TYPE_ANSWERS, GLASS_BREAKAGE_DAMAGE_TYPE_IMAGE_CHOICES, GLASS_BREAKAGE_DAMAGE_TYPE_OPTIONS, GLASS_BREAKAGE_TRAD_KEYS, } from '@declaration/constants/glassBreakageDamageConstants';
import { GlassBreakageDamageType } from '@shared/types/api/glassBreakageDamage';
import { InsurerFeatureFlag, insurerHasFeature } from '@shared/featureFlags/insurerFeatureFlags';
const claim = namespace('claim');
const qualification = namespace('qualification');
const settings = namespace('settings');
let GlassBreakageDamageTypeQuestion = class GlassBreakageDamageTypeQuestion extends QuestionComponent {
    constructor() {
        super(...arguments);
        this.isSaving = false;
        this.typeOfDamages = '';
    }
    get selfCareSettings() {
        return this.setting('selfCareSettings');
    }
    get typeTrigram() {
        var _a;
        return (_a = this.claimTypeTrigram) !== null && _a !== void 0 ? _a : undefined;
    }
    get hideHelpLink() {
        return insurerHasFeature(InsurerFeatureFlag.HideHelpLink, this.typeTrigram);
    }
    get showHelpLink() {
        var _a, _b, _c, _d;
        if (this.hideHelpLink &&
            ((_d = (_c = (_b = (_a = this.selfCareSettings) === null || _a === void 0 ? void 0 : _a.declaration) === null || _b === void 0 ? void 0 : _b.pages) === null || _c === void 0 ? void 0 : _c.glassBreakageDamageType) === null || _d === void 0 ? void 0 : _d.showHelpLink) === false) {
            return false;
        }
        return true;
    }
    get panelTitle() {
        return this.$tc(GLASS_BREAKAGE_TRAD_KEYS['damage-type-panel-title']);
    }
    get sidebarTitle() {
        const key = GLASS_BREAKAGE_TRAD_KEYS['damage-type-question-sidebar_title'];
        return this.$te(key) ? this.$tc(key) : this.panelTitle;
    }
    get questionDescription() {
        return this.$tc(GLASS_BREAKAGE_TRAD_KEYS['damage-type-question-description']);
    }
    get typeOfDamagesChoices() {
        return GLASS_BREAKAGE_DAMAGE_TYPE_IMAGE_CHOICES.map((choice) => ({
            value: choice.value,
            label: this.$tc(choice.tradKey),
            imageSrc: choice.image,
            description: this.$tc(GLASS_BREAKAGE_DAMAGE_TYPE_OPTIONS[choice.value].descriptionTradKey),
        }));
    }
    get selectedValues() {
        const values = [];
        if (this.qualification.hasRoomDamages)
            values.push(GlassBreakageDamageType.RoomDamages);
        if (this.qualification.hasPersonalPropertyDamages)
            values.push(GlassBreakageDamageType.PersonalProperties);
        return values;
    }
    get answer() {
        if (this.qualification.hasRoomDamages && this.qualification.hasPersonalPropertyDamages) {
            return this.$tc(GLASS_BREAKAGE_DAMAGE_TYPE_ANSWERS.both);
        }
        if (this.qualification.hasRoomDamages) {
            return this.$tc(GLASS_BREAKAGE_DAMAGE_TYPE_ANSWERS[GlassBreakageDamageType.RoomDamages]);
        }
        if (this.qualification.hasPersonalPropertyDamages) {
            return this.$tc(GLASS_BREAKAGE_DAMAGE_TYPE_ANSWERS[GlassBreakageDamageType.PersonalProperties]);
        }
        return null;
    }
    onTypeOfDamagesChange(values) {
        this.updateGlassBreakageDamageQualification({
            qualificationPayload: {
                hasRoomDamages: values.includes(GlassBreakageDamageType.RoomDamages),
                hasPersonalPropertyDamages: values.includes(GlassBreakageDamageType.PersonalProperties),
            },
        });
    }
    onBothButtonClick() {
        this.updateGlassBreakageDamageQualification({
            qualificationPayload: {
                hasRoomDamages: true,
                hasPersonalPropertyDamages: true,
            },
        });
        this.validate();
    }
    async validate() {
        await this.updateAndSaveGlassBreakageDamageQualification({
            qualificationPayload: {
                hasRoomDamages: this.qualification.hasRoomDamages,
                hasPersonalPropertyDamages: this.qualification.hasPersonalPropertyDamages,
            },
            step: this.step,
        });
    }
};
__decorate([
    claim.State('isUpdatingClaim')
], GlassBreakageDamageTypeQuestion.prototype, "isUpdatingClaim", void 0);
__decorate([
    claim.State('claimTypeTrigram')
], GlassBreakageDamageTypeQuestion.prototype, "claimTypeTrigram", void 0);
__decorate([
    qualification.State('qualification')
], GlassBreakageDamageTypeQuestion.prototype, "qualification", void 0);
__decorate([
    qualification.Action('updateQualification')
], GlassBreakageDamageTypeQuestion.prototype, "updateGlassBreakageDamageQualification", void 0);
__decorate([
    qualification.Action('updateAndSaveQualification')
], GlassBreakageDamageTypeQuestion.prototype, "updateAndSaveGlassBreakageDamageQualification", void 0);
__decorate([
    settings.Getter('setting')
], GlassBreakageDamageTypeQuestion.prototype, "setting", void 0);
GlassBreakageDamageTypeQuestion = __decorate([
    Component({
        components: { QuestionPanel, UiButton, UiCheckbox },
        props: QuestionProps,
    })
], GlassBreakageDamageTypeQuestion);
export default GlassBreakageDamageTypeQuestion;
